import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, interval, BehaviorSubject, of } from 'rxjs';
import { catchError, map, switchMap, startWith } from 'rxjs/operators';
import { environment } from '../../../src/environments/environment';

export interface MessageTarget {
  id: number;
  name: string;
  avatar?: string;
  type: string;
  role?: string;
  firstname?: string;
  lastname?: string;
}

export interface RawConversationResponse {
  id: number;
  idFriend: number;
  idUsersConverstion: string;
  lastDate: string;
  lastMessage: string;
  nameFriend: string;
  numberMessage: number;
  seen: number;
}

export interface Message {
  active: number;
  any: any[];
  anyNs: any[];
  conversation: {
    id: number;
    idUsersConverstion: string;
    idUser1: number;
    idUser2: number;
    nameUser1: string;
    nameUser2: string;
  };
  creationDate: Date;
  id: number;
  listPieceJoint: { pieceJoint: any[] };
  messageContent: string;
  user: {
    id: number;
    firstname: string;
    lastname: string;
  };
  withPieceJoint: number;
}

export interface Conversation {
  id: number;
  idFriend: number;
  friendName: string;
  lastMessageContent?: string;
  lastMessageDate?: Date;
  unreadCount: number;
  messages: Message[];
  avatar?: string;
}

export interface AddMessageRequest {
  idConversation: number;
  idUserStudent: number;
  idUserFriend: number;
  contentMessage: string;
  idOrganization: number;
}

export interface ApiResponseConversations {
  error: string;
  response: RawConversationResponse[];
}

export interface ApiResponseMessages {
  error: string;
  response: Message[];
}

@Injectable({
  providedIn: 'root',
})
export class MessagerieService {
  private apiUrl = environment.apiUrl;
  private messageUpdatesSubject = new BehaviorSubject<Message[]>([]);
  public messageUpdates$ = this.messageUpdatesSubject.asObservable();
  private pollingInterval = 5000; // Poll every 5 seconds
  
  constructor(private http: HttpClient) {
  }

  private getHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };
  }

  private handleError(error: any): Observable<never> {
    return throwError(() => new Error(error.message || 'Server error'));
  }

  // Get list of conversations for a student
  getConversations(idUserStudent: number): Observable<Conversation[]> {
    const url = `${this.apiUrl}/chat/listConversation/${idUserStudent}`;

    return this.http.get<ApiResponseConversations>(url, this.getHttpOptions()).pipe(
      map(apiResponse => {
        return apiResponse.response.map(conversation => ({
          id: conversation.id,
          idFriend: conversation.idFriend,
          friendName: conversation.nameFriend,
          lastMessageContent: conversation.lastMessage,
          lastMessageDate: conversation.lastDate ? new Date(conversation.lastDate) : undefined,
          unreadCount: conversation.numberMessage,
          messages: [], // Messages will be loaded separately
          avatar: undefined // Assuming avatar is not in this response
        }));
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Get list of users that can be messaged
  getMessageTargets(idOrganization: number): Observable<MessageTarget[]> {
    const url = `${this.apiUrl}/user/listUserEdulink/${idOrganization}/0`;
    
    return this.http.get<{ error: string, response: MessageTarget[] }>(url, this.getHttpOptions()).pipe(
      map(data => {
        const response = data.response;
        return response.map((target: MessageTarget) => ({
          ...target,
          name: `${target.firstname || ''} ${target.lastname || ''}`.trim(),
          avatar: target.avatar || 'assets/default-avatar.png'
        }));
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Mark conversation as seen
  markConversationAsSeen(idStudentUser: number, idConversation: number): Observable<any> {
    const url = `${this.apiUrl}/chat/vueLastMessage/${idConversation}/${idStudentUser}`;
    
    return this.http.get(url, this.getHttpOptions()).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Send a message with optional files
  sendMessage(messageRequest: AddMessageRequest, files?: File[]): Observable<any> {
    console.log('Sending message with request:', messageRequest, 'and files:', files);
    const url = `${this.apiUrl}/chat/createMessageEdulink`;
    console.log('API URL for sending message:', url);
    const formData = new FormData();

    // Append each message parameter as a separate field
    formData.append('idConversation', messageRequest.idConversation.toString());
    formData.append('idUserStudent', messageRequest.idUserStudent.toString());
    formData.append('idUserFriend', messageRequest.idUserFriend.toString());
    formData.append('contentMessage', messageRequest.contentMessage);
    formData.append('idOrganization', messageRequest.idOrganization.toString());
    
    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append('files', file, file.name);
      });
    }
    
    return this.http.post(url, formData).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Get messages by friend (for new conversations or when we don\'t know if conversation exists)
  getMessagesByFriend(idStudentUser: number, idFriend: number): Observable<Message[]> {
    const url = `${this.apiUrl}/chat/listMessageByUser/${idStudentUser}/${idFriend}/1`;
    
    return this.http.get<Message[]>(url, this.getHttpOptions()).pipe(
      map(messages => {
        return messages.map(message => ({
          ...message,
          creationDate: new Date(message.creationDate)
        }));
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Get messages by conversation (for existing conversations)
  getMessagesByConversation(idConversation: number): Observable<Message[]> {
    const url = `${this.apiUrl}/chat/listMessageByConversation/${idConversation}/1`;
    
    return this.http.get<ApiResponseMessages>(url, this.getHttpOptions()).pipe(
      map(apiResponse => {
        return apiResponse.response.map(message => ({
          ...message,
          creationDate: new Date(message.creationDate)
        }));
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  // Check if conversation exists between user and friend
  checkConversationExists(idStudentUser: number, idFriend: number): Observable<{exists: boolean, conversationId?: number}> {
    return this.getMessagesByFriend(idStudentUser, idFriend).pipe(
      map(messages => {
        const result = messages && messages.length > 0 ? 
          { exists: true, conversationId: messages[0].conversation.id } : 
          { exists: false };
        return result;
      }),
      catchError(error => {
        return of({ exists: false }); // Return an observable of { exists: false } on error
      })
    );
  }

  // Start polling for new messages in a conversation
  startMessagePolling(idConversation: number): Observable<Message[]> {
    return interval(this.pollingInterval).pipe(
      startWith(0),
      switchMap(() => this.getMessagesByConversation(idConversation)),
      catchError(error => {
        return [];
      })
    );
  }
  
}
